import { useTranslation } from 'react-i18next';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { formatRate } from '@/lib/metrics';
import { currentLocale, formatNumber } from '@/i18n/locale';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import type { QualificationAccuracy } from '@/api/endpoints/analytics';
import { errorCode } from './LoadError';

type Row = QualificationAccuracy['outcomes'][number];

/**
 * « Précision IA » (CEO-12): per outcome the AI suggested on the call page,
 * how often the final outcome kept it, and what the agents chose instead. A
 * « Corriger » after the fact counts as changed. Counted by the server over
 * the qualifications of the period (POST /api/analytics/dialer/qualification-accuracy).
 */
export default function QualificationAccuracyTable({ data, state, onRetry, error }: { data: QualificationAccuracy | undefined; state: 'loading' | 'error' | 'ready'; onRetry: () => void; error?: unknown }) {
    const { t } = useTranslation(['dialer', 'common']);
    const locale = currentLocale();

    const columns: DataTableColumn<Row>[] = [
        { id: 'suggested', header: t('dialer:analytics.accuracy.suggested'), cell: (r) => outcomeLabel(r.suggested, t) },
        { id: 'total', header: t('dialer:analytics.accuracy.total'), cell: (r) => formatNumber(r.total), align: 'right' },
        { id: 'accepted', header: t('dialer:analytics.accuracy.accepted'), cell: (r) => formatNumber(r.accepted), align: 'right' },
        { id: 'changed', header: t('dialer:analytics.accuracy.changed'), cell: (r) => formatNumber(r.changed), align: 'right' },
        { id: 'rate', header: t('dialer:analytics.accuracy.rate'), cell: (r) => formatRate(r.acceptanceRate, locale), align: 'right' },
        {
            id: 'changedTo', header: t('dialer:analytics.accuracy.changedTo'),
            cell: (r) => (r.changedTo.length === 0 ? '—' : r.changedTo.map((c) => `${outcomeLabel(c.outcome, t)} (${formatNumber(c.count)})`).join(', ')),
        },
    ];

    return (
        <section className="space-y-3" aria-labelledby="analytics-accuracy-title">
            <div>
                <h2 id="analytics-accuracy-title" className="text-sm font-semibold text-foreground">
                    {data && data.total > 0
                        ? t('dialer:analytics.accuracy.finding', { rate: formatRate(data.acceptanceRate, locale), count: data.total, total: formatNumber(data.total) })
                        : t('dialer:analytics.accuracy.title')}
                </h2>
                <p className="text-xs text-muted-foreground">{t('dialer:analytics.accuracy.subtitle')}</p>
            </div>
            <DataTable
                caption={t('dialer:analytics.accuracy.title')}
                columns={columns}
                rows={data?.outcomes ?? []}
                getRowId={(r) => r.suggested}
                status={state}
                error={{ message: t('dialer:analytics.loadError'), code: errorCode(error), onRetry }}
                empty={{ title: t('dialer:analytics.accuracy.empty') }}
            />
        </section>
    );
}
